<template>
    <div class="box default-player-settings">
        <h2 class="box-title">
            Default Player Settings
        </h2>
        <div class="box-content">
            <p class="description">
                Choose your default player settings (you can override these settings in individual player options):
            </p>
            <form
                id="player-settings"
                novalidate
                @submit.prevent="saveInfo"
            >
                <base-settings-row
                    config-title="Player controls"
                    input-type="checkbox"
                    :fields-config="controlsConfig"
                    :binded-value="config.controls"
                    settings-key="controls"
                    :is-active="hasSpeedRewind"
                    @change="value => controlsChange(value)"
                />
                <base-settings-row
                    config-title="Buttons"
                    input-type="checkbox"
                    :fields-config="buttonsConfig"
                    :binded-value="config.buttons"
                    settings-key="buttons"
                    :available-options="availableButtons"
                    @change="value => controlsChange(value)"
                />
                <div class="settings-field">
                    <p class="title">
                        Cover Artwork:
                    </p>
                    <div class="options">
                        <base-checkbox
                            :container-class="['settings-option']"
                            label="Show cover"
                            :value="true"
                            :values="config.coverDisplay"
                            @change="value => config.coverDisplay = value"
                        />
                        <div
                            v-if="config.coverDisplay"
                            class="row-container is-centered"
                        >
                            <base-checkbox
                                :container-class="['settings-option']"
                                label="Go to URL on click"
                                :value="true"
                                :values="config.cover.link"
                                @change="value => config.cover.link = value"
                            />
                            <input
                                v-model="config.cover.url"
                                type="text"
                                placeholder="https://"
                                :disabled="! config.cover.link"
                                @blur="config.cover.url = prefixUrlInput(config.cover.url)"
                            >
                        </div>
                    </div>
                </div>
                <base-settings-row
                    config-title="Fusebox credit"
                    input-type="checkbox"
                    :fields-config="creditConfig"
                    :binded-value="config.credits"
                    settings-key="credits"
                    :is-active="hasHiddenCredits"
                    @change="value => controlsChange(value)"
                />
                <div class="controll-container flex">
                    <base-form-button
                        class="btn"
                        :loading="formProcessing"
                    >
                        Save Changes
                    </base-form-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import playerSettings from '@/utils/config/playerSettings';
import toasts from '@/mixins/toasts';

import { isEqual } from 'lodash';
import prefixUrlInput from '@/utils/form/prefixUrlInput';
import { Feature, hasFeature } from '@/utils/show';

const {
    controlsConfig,
    buttonsConfig,
    freeOptions,
} = playerSettings;

export default {
    mixins: [toasts],

    props: {
        show: {
            type: Object,
            default: () => ({}),
        },
        formProcessing: {
            type: Boolean,
            required: true,
        },
    },

    data: () => ({
        controlsConfig,
        buttonsConfig,
        freeOptions,
        creditConfig: [
            {
                label: 'Show “Powered by Fusebox”',
            },
        ],
        config: {
            controls: [],
            buttons: [],
            cover: {
                imageUrl: '',
                link: false,
                url: null,
            },
            coverDisplay: false,
            credits: true,
        },
    }),

    computed: {
        /**
         * Checks if the show has speed/rewind controls feature enabled.
         *
         * @returns {boolean} True if the show has speed/rewind controls feature.
         */
        hasSpeedRewind() {
            return hasFeature(this.show, Feature.SpeedRewind);
        },

        /**
         * Checks if the show has hidden credits feature enabled.
         *
         * @returns {boolean} True if the show has hidden credits feature.
         */
        hasHiddenCredits() {
            return hasFeature(this.show, Feature.HiddenPoweredBy);
        },

        /**
         * Returns the list of available buttons.
         *
         * @returns {Array} List of available buttons.
         */
        availableButtons() {
            return [
                hasFeature(this.show, Feature.DownloadButton) && 'download',
                hasFeature(this.show, Feature.ShareButton) && 'share',
                hasFeature(this.show, Feature.SubscribeButton) && 'subscribe',
            ].filter((button) => !! button);
        },
    },

    watch: {
        show: {
            immediate: true,

            /**
             * Manage data.
             *
             * @param {object} show Show object data.
             * @param {*} oldShow Show object data.
             */
            handler(show, oldShow) {
                if (isEqual(show, oldShow)) {
                    return;
                }

                this.config.controls = show.controls;
                this.config.buttons = show.buttons;
                this.config.coverDisplay = show.coverDisplay;
                this.config.credits = show.credits;
            },
        },
    },

    methods: {
        prefixUrlInput,

        /**
         * Submit form
         *
         * @returns {void} Do nothing, now.
         */
        saveInfo() {
            if (! this.validate()) {
                return;
            }

            this.$emit('submit', this.config);
        },

        /**
         * Sets component data based on configuration object.
         *
         * @param {object} config  Object with configuration
         */
        controlsChange(config) {
            for (const key in config) {
                this.config[key] = config[key];
            }
        },

        /**
         * Validate custom link
         *
         * @returns  {boolean}  If field has data
         */
        validate() {
            if (this.config.cover.link && ! this.config.cover.url) {
                this.toastError({
                    message: {
                        title: 'Please enter Cover Artwork link',
                    },
                });

                return false;
            }

            return true;
        },
    },
};
</script>
